import { useEffect, useMemo, useState, useRef } from 'react';
import { ENDPOINTS } from '../../../Utils/Endpoints';
import { Models } from '../../../Utils/ModelsLMS';
import ConfirmDeleteDialog from '../../../../security/ConfirmDeleteDialog';
import { normalizeValue } from '../../../../../utils/Texts';
import axios from 'axios';
import Modal from './EstudiantesCursosModal';
import sigmaDelete from '../../../../Pages/img/Borrar.png';
import sigmaEdit from '../../../../Pages/img/Editar.png';
import { API_BASE } from '../../../../../api';
import AlertDialog from '../../Shared/AlertDialog';

const PAGE_SIZE = 10;
const nombreTabla = "Catálogo de Estudiantes - Cursos";
const endpoint = ENDPOINTS.estudiantesCurso; // Verifica que sea el correcto en Endpoints.js
// Si no existe un endpoint específico por ID para delete, se usa el genérico con query param o path
// Asumiremos standard REST: endpoint + "/{id}"
const endpointById = (id) => `${endpoint}/${id}`;
const modelo = Models.estudiantesCurso;

const maps = {
    mapCreate: (f) => ({
        idEstudiante: Number(f.idEstudiante) || 0,
        idCurso: Number(f.idCurso) || 0,
        calificacionFinal: Number(f.calificacionFinal) || 0,
        idEstado: Number(f.idEstado) || 0,
        certificadoGenerado: f.certificadoGenerado || false,
        ultimoAcceso: f.ultimoAcceso ? new Date(f.ultimoAcceso).toISOString() : null,
        fechaTermino: f.fechaTermino ? new Date(f.fechaTermino).toISOString() : null,
        activo: f.activo !== undefined ? f.activo : true,
    }),
    mapUpdate: (id, f) => ({
        idEstudiantesCursos: id,
        idEstudiante: Number(f.idEstudiante) || 0,
        idCurso: Number(f.idCurso) || 0,
        calificacionFinal: Number(f.calificacionFinal) || 0,
        idEstado: Number(f.idEstado) || 0,
        certificadoGenerado: f.certificadoGenerado || false,
        ultimoAcceso: f.ultimoAcceso ? new Date(f.ultimoAcceso).toISOString() : null,
        fechaTermino: f.fechaTermino ? new Date(f.fechaTermino).toISOString() : null,
        activo: f.activo !== undefined ? f.activo : true,
    }),
};

export default function EstudiantesCursosCatalog({ studentIdFilter = null }) {
    const [loading, setLoading] = useState(false);
    const [rows, setRows] = useState([]);
    const [query, setQuery] = useState('');
    const [modalOpen, setModalOpen] = useState(false);
    const [modalMode, setModalMode] = useState('new');
    const [selectedId, setSelectedId] = useState(null);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState({ id: 0, name: '' });
    const [editData, setEditData] = useState(null);
    const [page, setPage] = useState(1);
    const [alertConfig, setAlertConfig] = useState({ open: false, message: '', title: 'Información', variant: 'info' });

    const triggerAlert = (message, variant = 'error', title = 'Error') => {
        setModalOpen(false);
        setDeleteOpen(false);
        setAlertConfig({ open: true, message, variant, title });
    };

    // Catálogos auxiliares para mostrar nombres en la tabla
    const [catCursos, setCatCursos] = useState([]);
    const [catEstudiantes, setCatEstudiantes] = useState([]);
    const [catEstados, setCatEstados] = useState([]);
    const [catPersonas, setCatPersonas] = useState([]);
    const [catEmpleados, setCatEmpleados] = useState([]);

    const editorRef = useRef(null);

    useEffect(() => {
        if (modalOpen && editorRef.current) {
            editorRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [modalOpen, selectedId]);

    const loadData = async () => {
        setLoading(true);
        try {
            // Cargar datos principales y catálogos necesarios para resolución de nombres
            const [resMain, resCur, resEst, resEdo, resEmp, resPers] = await Promise.all([
                axios.get(endpoint),
                axios.get(ENDPOINTS.cursos),
                axios.get(ENDPOINTS.estudiante), // Asegurarse que exista en Endpoints
                axios.get(ENDPOINTS.catEstados),  // Asegurarse que exista
                axios.get(`${API_BASE}/Empleados`), // Necesario para ligar estudiante -> persona
                axios.get(`${API_BASE}/Personas`),
            ]);

            const dataMain = (resMain.data || []).map((x) => modelo(x));

            const cursosData = (resCur.data || []).map(Models.cursos);
            const estudiantesData = (resEst.data || []).map(Models.estudiante);
            const estadosData = (resEdo.data || []).map(Models.estados);
            const empleadosData = (resEmp.data || []).map(Models.empleado);
            const personasData = (resPers.data || []).map(Models.personas);

            setCatCursos(cursosData);
            setCatEstudiantes(estudiantesData);
            setCatEstados(estadosData);
            setCatEmpleados(empleadosData);
            setCatPersonas(personasData);

            // Enriquecer datos con nombres
            const enriched = dataMain.map((row) => {
                // Resolver nombre curso
                const curso = cursosData.find(c => c.idCurso === row.idCurso);
                const nombreCurso = curso ? curso.nombre : `Curso #${row.idCurso}`;

                // Resolver nombre estudiante
                // Estudiante -> Empleado -> Persona
                let nombreEstudiante = `Estudiante #${row.idEstudiante}`;
                const estudiante = estudiantesData.find(e => e.idEstudiante === row.idEstudiante);
                if (estudiante) {
                    const empleado = empleadosData.find(emp => emp.idEmpleado === estudiante.idEmpleado);
                    if (empleado) {
                        const persona = personasData.find(p => p.idPersona === empleado.idPersona);
                        if (persona) {
                            nombreEstudiante = `${persona.nombre} ${persona.apellidoPaterno} ${persona.apellidoMaterno || ''}`.trim();
                        }
                    }
                }

                // Resolver estado
                const estado = estadosData.find(e => e.idEstado === row.idEstado);
                const nombreEstado = estado ? estado.nombre : `Estado #${row.idEstado}`;

                return {
                    ...row,
                    nombreCurso,
                    nombreEstudiante,
                    nombreEstado
                };
            });

            setRows(enriched);
        } catch (e) {
            console.error(e);
            const msg = e.response?.data?.message || e.message || 'Error al cargar información';
            triggerAlert(`Error al cargar: ${msg}`);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { loadData(); }, []);

    const filtered = useMemo(() => {
        let base = rows;
        if (studentIdFilter) {
            base = base.filter(r => r.idEstudiante === Number(studentIdFilter));
        }
        const q = query.trim().toLowerCase();
        if (!q) return base;
        return base.filter((r) => {
            const curso = (r.nombreCurso || '').toLowerCase();
            const estudiante = (r.nombreEstudiante || '').toLowerCase();
            const estado = (r.nombreEstado || '').toLowerCase();
            return (
                curso.includes(q) ||
                estudiante.includes(q) ||
                estado.includes(q)
            );
        });
    }, [rows, query, studentIdFilter]);

    const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
    useEffect(() => { if (page > totalPages) setPage(1); }, [totalPages, page]);

    const paged = useMemo(() => {
        const start = (page - 1) * PAGE_SIZE;
        return filtered.slice(start, start + PAGE_SIZE);
    }, [filtered, page]);

    const handleNew = () => {
        setSelectedId(null);
        setEditData(null);
        setModalMode('new');
        setModalOpen(true);
    };

    const handleEdit = (id) => {
        const r = rows.find(x => x.idEstudiantesCursos === id);
        if (!r) {
            triggerAlert('No se encontró la fila en memoria.');
            return;
        }
        setSelectedId(id);
        setEditData(r);
        setModalMode('edit');
        setModalOpen(true);
    };

    const askDelete = (id, name) => {
        setDeleteTarget({ id, name });
        setDeleteOpen(true);
    };

    const doDelete = async () => {
        try {
            setLoading(true);
            await axios.delete(endpointById(deleteTarget.id));
            setDeleteOpen(false);
            await loadData();
        } catch (e) {
            console.error(e);
            const msg = e.response?.data?.message || e.message || 'Error al eliminar';
            triggerAlert(`Error al eliminar: ${msg}`);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (payload, mode) => {
        try {
            setLoading(true);
            if (mode === 'new') {
                await axios.post(endpoint, maps.mapCreate(payload));
            } else {
                await axios.put(endpoint, maps.mapUpdate(selectedId, payload));
            }
            setModalOpen(false);
            await loadData();
        } catch (e) {
            console.error(e);
            const errorMsg = e.response?.data?.message || e.response?.data || e.message || 'Error al guardar';
            triggerAlert(`Error al guardar: ${typeof errorMsg === 'object' ? JSON.stringify(errorMsg) : errorMsg}`);
        } finally {
            setLoading(false);
        }
    };

    const PageBtn = ({ label, onClick, active, disabled }) => (
        <button
            type="button"
            className={`roles-page-btn ${active ? 'is-active' : ''} ${disabled ? 'is-disabled' : ''}`}
            onClick={onClick}
            disabled={disabled}
        >
            {label}
        </button>
    );

    const pageNumbers = (() => {
        const maxToShow = 5;
        let start = Math.max(1, page - 2);
        let end = Math.min(totalPages, start + maxToShow - 1);
        start = Math.max(1, end - maxToShow + 1);
        return Array.from({ length: end - start + 1 }, (_, i) => start + i);
    })();

    const startIndex = filtered.length === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
    const endIndex = Math.min(page * PAGE_SIZE, filtered.length);

    const showStudentColumn = !studentIdFilter;

    return (
        <div className="panel-sigma-main" style={studentIdFilter ? { boxShadow: 'none', border: 'none' } : {}}>
            <div className="roles-shell" style={studentIdFilter ? { padding: 0 } : {}}>
                <div className="roles-topbar">
                    {!studentIdFilter && <h2 className="roles-title">{nombreTabla}</h2>}
                    {studentIdFilter && <h4 className="roles-subtitle" style={{ margin: 0, marginRight: 'auto' }}>Cursos del Estudiante</h4>}

                    <input
                        className="roles-search"
                        placeholder="Buscar por curso, estudiante o estado…"
                        value={query}
                        onChange={(e) => { setQuery(e.target.value); setPage(1); }}
                    />
                    <button className="btn-action" onClick={handleNew}>＋ Nuevo</button>
                </div>

                <div className="roles-table-wrap">
                    <div className="roles-table-scroller">
                        <table className="roles-table">
                            <thead>
                                <tr>
                                    {showStudentColumn && <th>Estudiante</th>}
                                    <th>Curso</th>
                                    <th>Calif.</th>
                                    <th>Estado</th>
                                    <th>Certificado</th>
                                    <th>Último Acceso</th>
                                    <th style={{ textAlign: 'center' }}>Editar</th>
                                    <th style={{ textAlign: 'center' }}>Borrar</th>
                                </tr>
                            </thead>
                            <tbody>
                                {paged.map((r) => (
                                    <tr key={r.idEstudiantesCursos}>
                                        {showStudentColumn && <td>{normalizeValue(r.nombreEstudiante)}</td>}
                                        <td><strong>{normalizeValue(r.nombreCurso)}</strong></td>
                                        <td>{r.calificacionFinal}%</td>
                                        <td>{normalizeValue(r.nombreEstado)}</td>
                                        <td>{r.certificadoGenerado ? '✅' : '—'}</td>
                                        <td>{r.ultimoAcceso ? new Date(r.ultimoAcceso).toLocaleDateString() : '—'}</td>
                                        <td style={{ textAlign: 'center' }}>
                                            <button
                                                type="button"
                                                className="rk-iconbtn rk-iconbtn--edit"
                                                onClick={() => handleEdit(r.idEstudiantesCursos)}
                                                aria-label="Editar"
                                                title="Editar"
                                            >
                                                <img src={sigmaEdit} alt="" className="rk-ico" />
                                            </button>
                                        </td>
                                        <td style={{ textAlign: 'center' }}>
                                            <button
                                                type="button"
                                                className="rk-iconbtn rk-iconbtn--del"
                                                onClick={() => {
                                                    askDelete(r.idEstudiantesCursos, `${r.nombreEstudiante} - ${r.nombreCurso}`);
                                                }}
                                                aria-label="Borrar"
                                                title="Borrar"
                                            >
                                                <img src={sigmaDelete} alt="" className="rk-ico" />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                                {!loading && paged.length === 0 && (
                                    <tr><td colSpan={showStudentColumn ? 8 : 7}>Sin resultados.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                <div className="roles-pagination">
                    <div className="roles-pagination-left">
                        {filtered.length > 0
                            ? <span>Mostrando {startIndex}–{endIndex} de {filtered.length}</span>
                            : <span>0 resultados</span>}
                    </div>
                    <div className="roles-pagination-right">
                        <PageBtn label="‹" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} />
                        {pageNumbers.map(n => (
                            <PageBtn
                                key={n}
                                label={n}
                                onClick={() => setPage(n)}
                                active={n === page}
                            />
                        ))}
                        <PageBtn
                            label="›"
                            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                            disabled={page === totalPages}
                        />
                    </div>
                </div>

                {modalOpen && (
                    <div className="roles-editor-slot" ref={editorRef}>
                        <Modal
                            open={modalOpen}
                            mode={modalMode}
                            data={editData}
                            inline
                            onClose={() => setModalOpen(false)}
                            onSubmit={handleSubmit}
                            fixedStudentId={studentIdFilter}
                            triggerAlert={triggerAlert}
                        />
                    </div>
                )}
            </div>

            <ConfirmDeleteDialog
                open={deleteOpen}
                roleName={deleteTarget.name}
                onClose={() => setDeleteOpen(false)}
                onConfirm={doDelete}
            />

            <AlertDialog
                open={alertConfig.open}
                title={alertConfig.title}
                message={alertConfig.message}
                variant={alertConfig.variant}
                onClose={() => setAlertConfig({ ...alertConfig, open: false })}
            />
        </div>
    );
}
